<script lang="ts">
  import { onDestroy, tick } from 'svelte';
  import {
    deleteMessage as apiDeleteMessage,
    editMessage as apiEditMessage,
    forwardMessage as apiForwardMessage,
    getMessages,
    markConversationRead,
    pinMessage as apiPinMessage,
    sendMessage,
    toggleReaction as apiToggleReaction,
    unpinMessage as apiUnpinMessage,
  } from './api';
  import type { ChatMessage, Conversation, ExerciseReference, MessagingUser } from './api';
  import { messagePreviewText } from './attachments';
  import ThreadHeader from './ThreadHeader.svelte';
  import MessageList from './MessageList.svelte';
  import ThreadComposer from './ThreadComposer.svelte';
  import PinnedList from './PinnedList.svelte';
  import ForwardPicker from './ForwardPicker.svelte';
  import { jumpToAndHighlight } from '@/Util/utils';

  // Renders + manages one conversation's message thread (header/pinned/list/composer/realtime/
  // reply/edit/delete/react/pin/forward) for the centralized Messages page. Callers should wrap
  // this in `{#key conversation.id}` (same convention as CommentThreadPanel's callers) so
  // switching conversations remounts it fresh rather than needing to react to the `conversation`
  // prop changing in place.
  export let conversation: Conversation;
  export let currentUserId: number;
  // Candidate destinations for the forward picker -- the parent's full conversation list; this
  // pane filters out the currently-open one itself.
  export let allConversations: Conversation[] = [];
  // Called on every send and every live-received message, so a parent maintaining its own
  // conversation list can reorder/update that conversation's preview without this pane needing to
  // know anything about lists.
  export let onActivity: (conversationId: string, preview: string, at: string) => void = () => {};
  export let showBackButton: boolean = false;
  export let onBack: () => void = () => {};
  // Otherwise unhandled errors surface via ErrorBanner in the parent.
  export let onError: (message: string) => void = () => {};
  // Called every time this conversation gets marked read (on open, and on a live incoming
  // message while already open) -- lets a parent maintaining its own conversation list (with its
  // own unread_count) clear that badge locally instead of waiting on an unrelated refetch.
  export let onRead: (conversationId: string) => void = () => {};

  //@ts-ignore
  const Xert = window.Xert;

  let messages: ChatMessage[] = [];
  let loading = true;
  let sending = false;
  let scrollEl: HTMLElement | undefined;
  // The id of the first still-unread message at the moment this pane opened -- computed once in
  // init() (before marking read) and never recomputed, so the "N unread" divider stays put where
  // you left off instead of chasing you down the list as more get marked read.
  let unreadDividerId: string | null = null;

  let replyTo: ChatMessage | null = null;
  let editing: ChatMessage | null = null;
  let forwarding: ChatMessage | null = null;
  let showForwardPicker = false;

  function toReplyPreview(m: ChatMessage | null) {
    return m ? { id: m.id, label: participant(m.sender_id)?.first_name ?? 'them', preview: m.body ?? '[attachment]' } : null;
  }
  function toEditPreview(m: ChatMessage | null) {
    return m ? { id: m.id, body: m.body ?? '' } : null;
  }
  $: replyToForComposer = toReplyPreview(replyTo);
  $: editingForComposer = toEditPreview(editing);

  async function scrollToBottom() {
    await tick();
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
  }

  // Notifies both the parent's own list (onRead, for whichever embed this pane is in -- the
  // centralized Messages page, a club's Messages tab, ...) and the navbar Messages icon
  // (Xert.markMessageNotificationsRead(), added by an earlier change) that this conversation just
  // got marked read -- centralized here so every current and future caller gets it for free
  // instead of each one having to remember to wire it up itself.
  function notifyRead() {
    onRead(conversation.id);
    //@ts-ignore
    window.Xert?.markMessageNotificationsRead?.({ conversationId: conversation.id });
  }

  const onMessage = (event: { conversation_id: string; message: ChatMessage }) => {
    if (event.conversation_id !== conversation.id) return;
    messages = [...messages, event.message];
    onActivity(conversation.id, messagePreviewText(event.message), event.message.created_at);
    scrollToBottom();
    if (event.message.sender_id !== currentUserId) {
      void markConversationRead(conversation.id);
      notifyRead();
    }
  };

  const onMessageRead = (event: { conversation_id: string; read_by: number }) => {
    if (event.conversation_id !== conversation.id) return;
    messages = messages.map((m) => {
      if (m.sender_id !== currentUserId || m.read_by.some((r) => r.user_id === event.read_by)) return m;
      return { ...m, read_by: [...m.read_by, { user_id: event.read_by, at: new Date().toISOString() }] };
    });
  };

  // Shared by the message_edited/deleted/reacted/pinned/whatsapp_synced events -- all five carry
  // the same {conversation_id, message} shape (the service methods that trigger them return the
  // full updated message, same as a normal send), so one handler replaces the matching row in
  // place.
  const onMessageUpdated = (event: { conversation_id: string; message: ChatMessage }) => {
    if (event.conversation_id !== conversation.id) return;
    messages = messages.map((m) => (m.id === event.message.id ? event.message : m));
  };

  const channel = Xert._userChannel;
  if (channel) {
    channel.bind('message', onMessage);
    channel.bind('message_read', onMessageRead);
    channel.bind('message_edited', onMessageUpdated);
    channel.bind('message_deleted', onMessageUpdated);
    channel.bind('message_reacted', onMessageUpdated);
    channel.bind('message_pinned', onMessageUpdated);
    channel.bind('message_whatsapp_synced', onMessageUpdated);
  }
  onDestroy(() => {
    if (channel) {
      channel.unbind('message', onMessage);
      channel.unbind('message_read', onMessageRead);
      channel.unbind('message_edited', onMessageUpdated);
      channel.unbind('message_deleted', onMessageUpdated);
      channel.unbind('message_reacted', onMessageUpdated);
      channel.unbind('message_pinned', onMessageUpdated);
      channel.unbind('message_whatsapp_synced', onMessageUpdated);
    }
  });

  async function init() {
    try {
      messages = await getMessages(conversation.id);
      // Snapshot the unread boundary before marking read -- markConversationRead() never mutates
      // `messages` locally (it's a fire-and-forget POST), so this is safe to compute right here.
      const firstUnread = messages.find((m) => m.sender_id !== currentUserId && !m.read_by.some((r) => r.user_id === currentUserId));
      unreadDividerId = firstUnread?.id ?? null;
      // Flip loading before scrolling -- MessageList (and the scrollEl it binds) only mounts
      // once `!loading`, so calling scrollToBottom() any earlier finds scrollEl still undefined
      // and silently no-ops, leaving the pane at the top instead of jumping to the latest message.
      loading = false;
      await scrollToBottom();
      await markConversationRead(conversation.id);
      notifyRead();
    } catch (e) {
      onError('Unable to load messages right now.');
      console.log(e);
      loading = false;
    }
  }
  void init();

  function participant(userId: number) {
    return conversation.participants.find((p) => p.id === userId);
  }

  // For each participant (other than me), find the message with their most recent read_by
  // timestamp, and only attach their avatar there -- otherwise every reader's icon would
  // pile up on every message they'd read, not just the latest.
  $: readersByMessageId = computeReadersByMessage(messages, conversation, currentUserId);

  function computeReadersByMessage(msgs: ChatMessage[], conv: Conversation, selfId: number) {
    const map = new Map<string, MessagingUser[]>();

    // msgs is already chronological (oldest first), and a live batch-read (onMessageRead)
    // stamps every newly-read message with the same client-generated timestamp -- so on a
    // tie, prefer whichever was iterated later (>=, not >), since that's the newer message.
    const latestByReader = new Map<number, { messageId: string; at: number }>();
    for (const m of msgs) {
      for (const r of m.read_by) {
        if (r.user_id === selfId) continue;
        const at = new Date(r.at).getTime();
        const existing = latestByReader.get(r.user_id);
        if (!existing || at >= existing.at) {
          latestByReader.set(r.user_id, { messageId: m.id, at });
        }
      }
    }

    for (const [readerId, { messageId }] of latestByReader) {
      const user = conv.participants.find((p) => p.id === readerId);
      if (!user) continue;
      map.set(messageId, [...(map.get(messageId) ?? []), user]);
    }

    return map;
  }

  function readersFor(messageId: string): MessagingUser[] {
    return readersByMessageId.get(messageId) ?? [];
  }

  $: pinnedMessages = messages
    .filter((m) => m.pinned_at && !m.deleted_at)
    .sort((a, b) => new Date(b.pinned_at ?? 0).getTime() - new Date(a.pinned_at ?? 0).getTime());

  function pinnedSenderName(message: ChatMessage): string {
    if (message.sender_id === currentUserId) return 'You';
    const user = participant(message.sender_id);
    return user ? `${user.first_name} ${user.last_name}` : 'Unknown';
  }
  $: pinnedForList = pinnedMessages.map((m) => ({ id: m.id, label: pinnedSenderName(m), preview: m.body ?? '[attachment]' }));

  function handleUnpinById(messageId: string) {
    const message = messages.find((m) => m.id === messageId);
    if (message) handleUnpin(message);
  }

  async function send(text: string, files: File[], references: ExerciseReference[], replyToId?: string) {
    if (sending) return;
    sending = true;
    try {
      const message = await sendMessage(conversation.id, text, files, references, replyToId);
      messages = [...messages, message];
      onActivity(conversation.id, messagePreviewText(message), message.created_at);
      await scrollToBottom();
    } catch (e) {
      onError('Message failed to send.');
      console.log(e);
    } finally {
      sending = false;
    }
  }

  function handleReply(message: ChatMessage) {
    editing = null;
    replyTo = message;
  }

  function handleEdit(message: ChatMessage) {
    replyTo = null;
    editing = message;
  }

  function handleCancelEdit() {
    editing = null;
  }

  async function handleEditSubmit(id: string, body: string) {
    try {
      const updated = await apiEditMessage(id, body);
      messages = messages.map((m) => (m.id === id ? updated : m));
    } catch (e) {
      onError('Unable to save that edit.');
      console.log(e);
    }
  }

  async function handleDelete(message: ChatMessage) {
    try {
      await apiDeleteMessage(message.id);
      messages = messages.map((m) => (m.id === message.id ? { ...m, deleted_at: new Date().toISOString() } : m));
    } catch (e) {
      onError('Unable to delete that message.');
      console.log(e);
    }
  }

  async function handleReact(message: ChatMessage, emoji: string) {
    try {
      const updated = await apiToggleReaction(message.id, emoji);
      messages = messages.map((m) => (m.id === message.id ? updated : m));
    } catch (e) {
      onError('Unable to react to that message.');
      console.log(e);
    }
  }

  async function handlePin(message: ChatMessage) {
    try {
      const updated = await apiPinMessage(message.id);
      messages = messages.map((m) => (m.id === message.id ? updated : m));
    } catch (e) {
      onError('Unable to pin that message.');
      console.log(e);
    }
  }

  async function handleUnpin(message: ChatMessage) {
    try {
      const updated = await apiUnpinMessage(message.id);
      messages = messages.map((m) => (m.id === message.id ? updated : m));
    } catch (e) {
      onError('Unable to unpin that message.');
      console.log(e);
    }
  }

  function handleForward(message: ChatMessage) {
    forwarding = message;
    showForwardPicker = true;
  }

  async function handleForwardPick(conversationId: string) {
    if (!forwarding) return;
    try {
      await apiForwardMessage(forwarding.id, conversationId);
      if (conversation.id === conversationId) {
        // Forwarding into the currently-open conversation -- refetch so the forwarded copy
        // shows up immediately instead of waiting on the Pusher echo.
        messages = await getMessages(conversationId);
        await scrollToBottom();
      }
    } catch (e) {
      onError('Unable to forward that message.');
      console.log(e);
    } finally {
      forwarding = null;
    }
  }

  function handleJumpTo(messageId: string) {
    jumpToAndHighlight(`message-${messageId}`);
  }
</script>

{#if !loading}
  <ThreadHeader {conversation} {currentUserId} {showBackButton} {onBack} />
  <PinnedList pinned={pinnedForList} noun="message" onUnpin={handleUnpinById} onJumpTo={handleJumpTo} />
  <MessageList
    {messages}
    {currentUserId}
    participantById={participant}
    isGroup={conversation.type === 'group'}
    readersByMessage={readersFor}
    {unreadDividerId}
    bind:scrollEl
    onReply={handleReply}
    onEdit={handleEdit}
    onDelete={handleDelete}
    onReact={handleReact}
    onPin={handlePin}
    onUnpin={handleUnpin}
    onForward={handleForward}
    onJumpTo={handleJumpTo}
  />
  <ThreadComposer
    noun="message"
    {sending}
    onSend={send}
    onError={onError}
    replyTo={replyToForComposer}
    onCancelReply={() => (replyTo = null)}
    editing={editingForComposer}
    onCancelEdit={handleCancelEdit}
    onEdit={handleEditSubmit}
  />
{/if}

<ForwardPicker bind:open={showForwardPicker} conversations={allConversations.filter((c) => c.id !== conversation.id)} {currentUserId} onPick={handleForwardPick} />
